import React, { useEffect, useMemo, useState } from 'react';
import { OrganizationInput } from '../Subscriptions/components';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import { useAbstractAWSAccountsForm } from './AbstractAWSAccountsForm';
import { useNavigate } from 'react-router-dom';
import EnvironmentInput from './components/EnvironmentInput';
import { UserRole } from '../../models/enums/UserRole';
import { authenticatedUser } from '../../authentication/authModule';
import Organization from '../../models/entities/Organization';
import { getOrganizations } from '../common/OrganizationStorage';
import CartaInput from '../common/form-controls/Lookups/CartaInput';
import { Grid, Alert, TextInput, ToggleField, Button, Textarea, Select } from '../common';
import UserLookup from '../common/form-controls/Lookups/UserLookup';
import RequestFormWrapper from '../common/wrappers/RequestFormWrapper';
import GroupLookup from '../common/form-controls/Lookups/GroupLookup';
import { AWSEnvironment } from '../../models/enums/AWSEnvironment';
import MultipleSelectField from '../common/form-controls/MultipleSelectField';
import { Checkbox, CheckboxGroup } from '@emorg-prd/standard-react';
import './AWSAccountsCreate.css';
import { RequestAWSAccountModel } from '../../models/viewModels/AWSAccounts/RequestAWSAccountModel';
import EnvironmentConfiguration from '../../utils/EnvironmentConfiguration';
import StubGroupsService from '../../services/StubGroupService';
import GroupsService from '../../services/GroupsService';
import AWSAccountsService from '../../services/AWSAccountsService';
import { PermissionSetsResponse } from '../../models/viewModels/AWSAccounts/PermissionSetsResponse';
import Group from '../../models/Group';

const AWSAccountsCreate = () => {
    const { state, formModel, getStateFormHandlers, getLinkTree, isValid, submitAccount, setState } =
        useAbstractAWSAccountsForm();
    const stateFormHandler = () => getStateFormHandlers();
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [submissionAlert, setSubmissionAlert] = useState<AlertBanner>({ visible: false });
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [accountTypeOptions, setAccountTypeOptions] = useState<{ value: string; text: string }[]>([]);
    const [showAccNameAlert, setShowAccNameAlert] = useState<boolean>(true);
    const [termsChecked, setTermsChecked] = useState<boolean>(false);
    const [permissionSets, setPermissionSets] = useState<PermissionSetsResponse | null>(null);
    const [permissionSetsLoading, setPermissionSetsLoading] = useState<boolean>(false);
    const [requiredGroupAssignments, setRequiredGroupAssignments] = useState<Record<string, Group | null>>({});
    const [featureFlags, setFeatureFlags] = useState<{
        enableAwsTier0Creation: boolean;
        enableAwsCyberCriticalCreation: boolean;
    } | null>(null);
    const navigate = useNavigate();

    const groupsService = useMemo(
        () => (EnvironmentConfiguration.isDev() ? new StubGroupsService() : new GroupsService()),
        []
    );
    
    const [accountSubtypes, setAccountSubtypes] = useState({
        tier0: false,
        cyberCritical: false,
        ecn: false,
    });

    const awsAccountsService = useMemo(() => new AWSAccountsService(), []);

    const handleAlertClose = () => {
        setSubmissionAlert({ visible: false });
    };

    useEffect(() => {
        const fetchPermissionSets = async () => {
            if (state.stateForm.isManaged === undefined || state.stateForm.isManaged === null) {
                setPermissionSets(null);
                setRequiredGroupAssignments({});
                return;
            }
            
            setPermissionSetsLoading(true);
            try {
                const sets = await awsAccountsService.getPermissionSets(state.stateForm.isManaged);
                setPermissionSets(sets);
                
                const requiredSets = sets?.Always || [];
                const initialAssignments: Record<string, Group | null> = {};
                requiredSets.forEach(set => {
                    initialAssignments[set] = null;
                });
                setRequiredGroupAssignments(initialAssignments);
                
            } catch (error) {
                setPermissionSets({
                    Always: [],
                    ModificationOnly: [],
                    Other: []
                });
                setRequiredGroupAssignments({});
            } finally {
                setPermissionSetsLoading(false);
            }
        };

        fetchPermissionSets();
    }, [state.stateForm.isManaged, awsAccountsService]);

    const handleRequiredGroupChange = (permissionSet: string, group: Group | null) => {
        setRequiredGroupAssignments(prev => ({
            ...prev,
            [permissionSet]: group,
        }));
    };

    const areRequiredGroupsValid = (): boolean => {
        if (state.stateForm.isManaged === undefined || state.stateForm.isManaged === null) {
            return true;
        }
        
        if (permissionSetsLoading) {
            return true;
        }
        
        if (!permissionSets) {
            return true;
        }
        
        const requiredSets = permissionSets.Always || [];
        
        if (requiredSets.length === 0) {
            return true;
        }
        
        return requiredSets.every(set => {
            const assignment = requiredGroupAssignments[set];
            return assignment !== null && assignment !== undefined && assignment.id;
        });
    };

    const buildAccountGroupsPayload = (): Record<string, string[]> => {
        const accountGroups: Record<string, string[]> = {};
        
        Object.entries(requiredGroupAssignments).forEach(([permissionSet, group]) => {
            if (group && group.id) {
                accountGroups[permissionSet] = [group.id];
            }
        });
        
        return accountGroups;
    };

    const getAWSAccountFinalName = (): string => {
        const form = state.stateForm;
        
        if (form.accountName && form.accountName.trim().length > 0) {
            return form.accountName.toUpperCase();
        }
        
        const appName = form.appName?.trim().toUpperCase().replace(/\s+/g, '_') || '';
        const envName = form.environment?.name?.toUpperCase() || '';
        
        if (appName && envName) {
            return `${appName}_${envName}`;
        } else if (appName) {
            return appName;
        }
        
        return '';
    };

    const getAccountNamePreview = (): string | null => {
        const form = state.stateForm;
        
        if (!form.appName || form.appName.trim().length === 0) {
            return null;
        }
        
        if (form.accountName && form.accountName.trim().length > 0) {
            return null;
        }
        
        const appName = form.appName.trim().toUpperCase().replace(/\s+/g, '_');
        const envName = form.environment?.name?.toUpperCase() || '[ENVIRONMENT]';
        
        return `${appName}_${envName}`;
    };

    const handleSubmit = async () => {
        window.scroll(0, 0);
        setSubmitting(true);
        setSubmissionAlert({
            visible: true,
            text: 'Submitting request...',
            variant: 'default',
        });
        try {
            const model = formModel.create(RequestAWSAccountModel);
            
            model.accountGroups = buildAccountGroupsPayload();
            
            model.accountName = getAWSAccountFinalName();
            model.numOfDmzPublicIpAddresses = state.stateForm.numOfDmzPublicIpAddresses ?? '0';
            model.internalOrder = state.stateForm.internalOrder ?? '';
            
            model.isTier0 = accountSubtypes.tier0;
            model.isCyberCritical = accountSubtypes.cyberCritical;
            //model.isECN = accountSubtypes.ecn;
            
            await submitAccount(model);
            setSubmitting(false);
            setSubmissionAlert({
                visible: true,
                variant: 'success',
                text: 'Successfully Submitted',
            });
            setTimeout(() => {
                navigate('/awsAccounts');
            }, 5000);
        } catch (err: any) {
            const responseData = err?.response?.data;
            const responseDataObj = responseData as
                | {
                        title?: string;
                        message?: string;
                        errors?: Record<string, string[]>;
                  }
                | undefined;
            let errorText =
                responseDataObj?.title ||
                responseDataObj?.message ||
                (typeof responseData === 'string'
                    ? responseData
                    : 'An error occurred with your request. Please, try again later.');
            if (responseDataObj?.errors) {
                const details = Object.entries(responseDataObj.errors)
                    .flatMap(([field, messages]) => messages.map((msg) => `${field}: ${msg}`))
                    .join('\n');

                errorText = `${errorText}\n${details}`;
            }
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text: errorText,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubtypeChange = (
        type: 'tier0' | 'cyberCritical' | 'ecn',
        checked: boolean
    ) => {
        setAccountSubtypes(prev => ({
            ...prev,
            [type]: checked,
        }));
    };

    const showAWSAccountSuggestedName = (): boolean => {
        const form = state.stateForm;
        const requiredFields = [form.appName, form.environment];
        return requiredFields.every((field) => !!field) && showAccNameAlert;
    };

    const userNotAllowed = () => {
        setSubmissionAlert({
            visible: true,
            variant: 'error',
            text: 'This user is not allowed to enter this page.',
        });
        setTimeout(() => {
            navigate('/awsAccounts');
        }, 5000);
    };

    useEffect(() => {
        if (
            !authenticatedUser.isInRole(UserRole.AWSDisconnectedAccountRequestor) &&
            !authenticatedUser.isInRole(UserRole.AWSManagedAccountRequestor)
        )
            userNotAllowed();

        initFormModel();
    }, []);

    const initFormModel = async () => {
        formModel.fields.pocDurationDays.validation.required = () =>
            state.stateForm?.environment == AWSEnvironment.PoC && state.stateForm?.isManaged === false;

        try {
            const flags = await awsAccountsService.getFeatureFlags();
            setFeatureFlags(flags);
        } catch {
            setFeatureFlags({ enableAwsTier0Creation: false, enableAwsCyberCriticalCreation: false });
        }

        const organizations = (await getOrganizations()).map((o) => new Organization(o));
        setOrganizations(organizations);

        var accountTypeOptions: { value: string; text: string }[] = [];
        if (authenticatedUser.isInRole(UserRole.AWSManagedAccountRequestor)) {
            accountTypeOptions.push({
                value: 'true',
                text: 'Managed AWS',
            });
        }
        if (authenticatedUser.isInRole(UserRole.AWSDisconnectedAccountRequestor)) {
            accountTypeOptions.push({
                value: 'false',
                text: 'Disconnected AWS',
            });
        }
        setAccountTypeOptions(accountTypeOptions);
        setLoading(false);
    };

    const renderRequiredGroupInputs = () => {
        if (state.stateForm.isManaged === undefined || state.stateForm.isManaged === null) {
            return null;
        }
        
        if (permissionSetsLoading) {
            return <p>Loading permission sets...</p>;
        }
        
        if (!permissionSets) {
            return null;
        }
        
        const requiredSets = permissionSets.Always || [];
        if (requiredSets.length === 0) {
            return null;
        }

        return (
            <>
                <h4>Required Permission Set Assignments</h4>
                <Grid>
                    {requiredSets.map((permissionSet) => (
                        <Grid.Item key={permissionSet}>
                            <GroupLookup
                                label={`${permissionSet} *`}
                                disabled={submitting}
                                placeholder="Search for a group..."
                                note={`Azure AD group for ${permissionSet} permission set`}
                                value={requiredGroupAssignments[permissionSet]?.displayName || ''}
                                onChange={(group) => handleRequiredGroupChange(permissionSet, group)}
                                groupsService={groupsService}
                            />
                        </Grid.Item>
                    ))}
                </Grid>
                <br />
            </>
        );
    };

    const needsTermsAcceptance = (): boolean => {
        if (!state.stateForm.owner?.id) return false;
        return authenticatedUser.userId === state.stateForm.owner.id;
    };

    const isSubmitDisabled = (): boolean => {
        const formInvalid = !isValid();
        const groupsInvalid = !areRequiredGroupsValid();
        const termsRequired = needsTermsAcceptance() && !termsChecked;
        
        const hasAccountName = 
            (state.stateForm.appName && state.stateForm.appName.trim().length > 0) ||
            (state.stateForm.accountName && state.stateForm.accountName.trim().length > 0);
        
        return formInvalid || groupsInvalid || termsRequired || submitting || !hasAccountName;
    };

    const accountNamePreview = getAccountNamePreview();
    const canSelectTier0 = (): boolean => featureFlags?.enableAwsTier0Creation === true;
    const canSelectCyberCritical = (): boolean => featureFlags?.enableAwsCyberCriticalCreation === true;

    return (
        <RequestFormWrapper title="Request AWS Account" linkTree={getLinkTree()} loading={loading}>
            {submissionAlert.visible && (
                <Alert
                    variant={submissionAlert.variant}
                    body={submissionAlert.text}
                    hasButton
                    onClose={handleAlertClose}
                />
            )}
            <OrganizationInput
                organizationL3={stateFormHandler().selectedL3}
                organizationL4={stateFormHandler().selectedL4}
                globalOrganization={stateFormHandler().selectedOrganization}
                disabled={submitting}
            />
            <Grid variant="halves">
                <Grid.Item>
                    <ToggleField
                        id="account-type"
                        label="Account Type"
                        {...stateFormHandler().isManaged}
                        disabled={
                            submitting ||
                            (!authenticatedUser.isInRole(UserRole.AWSDisconnectedAccountRequestor) &&
                                !authenticatedUser.isInRole(UserRole.AWSManagedAccountRequestor) &&
                                !authenticatedUser.isInRole(UserRole.AWSAccountAdmin))
                        }
                        value={stateFormHandler().isManaged.value?.toString()}
                        options={
                            submitting && authenticatedUser.isInRole(UserRole.AWSAccountAdmin)
                                ? [
                                    { value: 'true', text: 'Managed AWS' },
                                    { value: 'false', text: 'Disconnected AWS' },
                                ]
                                : accountTypeOptions
                        }
                        note=""
                    />
                </Grid.Item>
                <Grid.Item>
                    <CheckboxGroup label="Account Subtypes">
                        {canSelectTier0() && (
                            <Checkbox
                                id="tier0"
                                value="Tier0"
                                label="Tier 0"
                                checked={accountSubtypes.tier0}
                                disabled={submitting || accountSubtypes.ecn}
                                onChange={(checked) =>
                                    handleSubtypeChange('tier0', checked)
                                }
                            />
                        )}
                        { canSelectCyberCritical() && (
                            <Checkbox
                                id="cyberCritical"
                                value="CyberCritical"
                                label="Cyber Critical"
                                checked={accountSubtypes.cyberCritical}
                                onChange={(checked) =>
                                    handleSubtypeChange('cyberCritical', checked)
                                }
                            />
                        )}
                        {/*<Checkbox
                            id="ecn"
                            value="ECN"
                            label="ECN"
                            checked={accountSubtypes.ecn}
                            disabled={accountSubtypes.tier0}
                            onChange={(checked) =>
                                handleSubtypeChange('ecn', checked)
                            }
                        /> */}
                    </CheckboxGroup>
                </Grid.Item>
            </Grid>
            {accountSubtypes.cyberCritical && (
                <Alert
                    variant="warning"
                    body={
                        'You have marked this account as Cyber Critical. The Account Owner/Custodian is responsible for updating Ardoq (CARTA) to flag the associated App ID as "Cyber Critical". The request may be delayed or rejected if the App ID is not correctly classified in Ardoq.'
                    }
                />
            )}
            <Grid>
                <Grid.Item>
                    <TextInput
                        label="Internal Order"
                        {...stateFormHandler().internalOrder}
                        disabled={submitting}
                        note="Enter the corresponding G-Code, otherwise it will be charged to EMIT L2"
                    />
                </Grid.Item>
                <Grid.Item>
                    <EnvironmentInput
                        isManaged={state.stateForm.isManaged}
                        {...stateFormHandler().environment}
                        disabled={submitting}
                    />
                </Grid.Item>
            </Grid>
            <TextInput
                label="AWS APP Name"
                {...stateFormHandler().appName}
                disabled={submitting}
                note="This field will be used to populate the account name, abbreviation can be used (Max 16 characters)"
            />
            {showAWSAccountSuggestedName() && (
                <Alert
                    body={`This field is auto-populated on every "Application Name" field change.

                    If you already have created an account for this application in another environment (i.e. Dev, Test/Acceptance, Production) use the same "Application name" for this request for the new environment for consistency.

                    Change it manually only if needed`}
                    onClose={() => setShowAccNameAlert(false)}
                />
            )}
            <TextInput 
                label="AWS Account Name" 
                {...stateFormHandler().accountName} 
                disabled={submitting}
                note="Leave empty to auto-generate from APP Name"
            />
            {accountNamePreview && (
                <div className="account-name-preview">
                    <strong>Account Name Preview:</strong> {accountNamePreview}
                </div>
            )}
            
            {renderRequiredGroupInputs()}
            
            <br />
            <Grid variant="2up">
                <Grid.Item>
                    <ToggleField
                        id="retained-app"
                        label="Will require Retained Operations (Non-MSP) support?"
                        {...stateFormHandler().isRetainedApp}
                        disabled={submitting}
                        value={stateFormHandler().isRetainedApp.value?.toString()}
                        options={[
                            { value: 'true', text: 'Yes' },
                            { value: 'false', text: 'No' },
                        ]}
                        note={''}
                    />
                </Grid.Item>
                {!stateFormHandler().isManaged.value ? (
                    <>
                        <Grid.Item>
                            <ToggleField
                                id="ec2-instances"
                                label="Requires EC2 Instances?"
                                {...stateFormHandler().requiresEc2Instances}
                                disabled={submitting}
                                value={stateFormHandler().requiresEc2Instances.value?.toString()}
                                options={[
                                    { value: 'true', text: 'Yes' },
                                    { value: 'false', text: 'No' },
                                ]}
                                note={''}
                            />
                        </Grid.Item>
                        <Grid.Item>
                            <ToggleField
                                id="public-ips"
                                label="Requires Public Ip Addresses?"
                                {...stateFormHandler().requiresPublicIpAddresses}
                                disabled={submitting}
                                value={stateFormHandler().requiresPublicIpAddresses.value?.toString()}
                                options={[
                                    { value: 'true', text: 'Yes' },
                                    { value: 'false', text: 'No' },
                                ]}
                                note={''}
                            />
                        </Grid.Item>
                        <Grid.Item>
                            <ToggleField
                                id="container-services"
                                label="Requires Native Container Services?"
                                {...stateFormHandler().requiresNativeContainerServices}
                                disabled={submitting}
                                value={stateFormHandler().requiresNativeContainerServices.value?.toString()}
                                options={[
                                    { value: 'true', text: 'Yes' },
                                    { value: 'false', text: 'No' },
                                ]}
                                note={''}
                            />
                        </Grid.Item>
                    </>
                ) : (
                    <>
                        <Grid.Item>
                            <ToggleField
                                id="network-isolation"
                                label="Requires Network Isolation?"
                                {...stateFormHandler().requiresIsolation}
                                disabled={submitting}
                                value={stateFormHandler().requiresIsolation.value?.toString()}
                                options={[
                                    { value: 'true', text: 'Yes' },
                                    { value: 'false', text: 'No' },
                                ]}
                                note={''}
                            />
                        </Grid.Item>
                        <Grid.Item>
                            <ToggleField
                                id="dmz"
                                label="DMZ Required?"
                                {...stateFormHandler().requiresDMZ}
                                disabled={submitting}
                                value={stateFormHandler().requiresDMZ.value?.toString()}
                                options={[
                                    { value: 'true', text: 'Yes' },
                                    { value: 'false', text: 'No' },
                                ]}
                                note={''}
                            />
                        </Grid.Item>
                        {(stateFormHandler().requiresDMZ.value?.toString() === 'true' ||
                            stateFormHandler().requiresIsolation.value?.toString() === 'true') && (
                            <Grid.Item>
                                <MultipleSelectField
                                    label="Region"
                                    {...stateFormHandler().regionNames}
                                    options={[
                                        { value: 'us-east-2', text: 'Ohio (us-east-2)' },
                                        { value: 'eu-west-2', text: 'London (eu-west-2)' },
                                        { value: 'ap-southeast-1', text: 'Singapore (ap-southeast-1)' },
                                        { value: 'ap-south-1', text: 'Mumbai (ap-south-1)' },
                                        { value: 'ap-southeast-5', text: 'Malaysia (ap-southeast-5)' },
                                        { value: 'ap-southeast-4', text: 'Melbourne (ap-southeast-4)' },
                                        { value: 'sa-east-1', text: 'Sao Paulo (sa-east-1)' },
                                    ]}
                                    disabled={submitting}
                                    fullWidth
                                    note={''}
                                />
                            </Grid.Item>
                        )}
                        {stateFormHandler().requiresDMZ.value?.toString() === 'true' && (
                            <Grid.Item>
                                <TextInput
                                    label="Number of Public Ips"
                                    {...stateFormHandler().numOfDmzPublicIpAddresses}
                                    disabled={submitting}
                                    note={''}
                                />
                            </Grid.Item>
                        )}
                        {(stateFormHandler().requiresDMZ.value?.toString() === 'true' ||
                            stateFormHandler().requiresIsolation.value?.toString() === 'true') && (
                            <Grid.Item>
                                <Select
                                    label="Number of Servers"
                                    {...stateFormHandler().vpcSizeId}
                                    disabled={submitting}
                                    options={[
                                        { value: '1', text: 'Small (1-10 servers)' },
                                        { value: '2', text: 'Medium (11-50 servers)' },
                                        { value: '3', text: 'Large (51-100 servers)' },
                                    ]}
                                />
                            </Grid.Item>
                        )}
                    </>
                )}
            </Grid>
            <br />
            <Grid variant="halves">
                <Grid.Item>
                    <CartaInput
                        {...stateFormHandler().cartaIds}
                        disabled={submitting}
                        oneOptionOnly={stateFormHandler().cartaIds.value?.length > 0}
                    />
                </Grid.Item>
                {state.stateForm.isManaged === false && state.stateForm.environment === AWSEnvironment.PoC && (
                    <Grid.Item>
                        <Select
                            label="PoC Duration"
                            {...stateFormHandler().pocDurationDays}
                            disabled={submitting}
                            options={[
                                { value: '30', text: '30' },
                                { value: '60', text: '60' },
                                { value: '90', text: '90' },
                            ]}
                            fullWidth
                        />
                    </Grid.Item>
                )}
            </Grid>
            <Grid variant="halves">
                <Grid.Item>
                    <UserLookup
                        label={'AWS Account Owner'}
                        {...stateFormHandler().owner}
                        disabled={submitting}
                        isAWSAccount
                        isAWSAccountOwner
                    />
                </Grid.Item>
                <Grid.Item>
                    <UserLookup
                        label={'AWS Account Custodian'}
                        {...stateFormHandler().custodian}
                        disabled={submitting}
                        isAWSAccount
                    />
                </Grid.Item>
            </Grid>
            <Textarea label="Business Justification" {...stateFormHandler().accountUsedFor} disabled={submitting} />
            {needsTermsAcceptance() && (
                <>
                    <CheckboxGroup
                        label="Terms and conditions"
                        required
                        optionalClass={termsChecked ? 'em-is-valid' : 'em-has-error'}
                    >
                        <Checkbox
                            value="1"
                            label="Check here to confirm that account owner and account custodian meets the eligibility requirements - Amazon Web Services (AWS) Account Owner Responsibilities | AppWiki"
                            id="check-terms"
                            checked={termsChecked}
                            onChange={(checked) => setTermsChecked(checked)}
                        />
                    </CheckboxGroup>
                    <>
                        For more info check this link:{' '}
                        <a
                            href="https://appwiki.xom.cloud/docs/SpecializedCloud/AWS/account-owner.html"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            AWS Account Owner Responsibilities
                        </a>
                    </>
                </>
            )}
            <Grid variant="4up">
                <Grid.Item>
                    <Button
                        variant="secondary"
                        onClick={() => navigate('/awsAccounts')}
                        isLoading={submitting}
                        loadingVariant="secondary"
                        disabled={submitting}
                        name="Cancel Request"
                    />
                </Grid.Item>
                <Grid.Item />
                <Grid.Item />
                <Grid.Item>
                    <Button
                        variant="primary"
                        disabled={isSubmitDisabled()}
                        isLoading={submitting}
                        onClick={handleSubmit}
                        loadingVariant="secondary"
                        name="Submit Request"
                    />
                </Grid.Item>
            </Grid>
        </RequestFormWrapper>
    );
};

export default AWSAccountsCreate;
